import { Box, ButtonBase, Typography, useMediaQuery } from '@mui/material'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { SaveBtn } from 'components/SaveBtn/SaveBtn'
import { FieldArray, Form, Formik } from 'formik'
import { usePutMasterProduct } from 'hooks/useMasterProducts'
import { t } from 'i18next'
import theme from 'lib/theme'
import { useAlert } from 'providers/AlertProvider'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { AddRelatedProductModal } from './AddRelatedProductModal'
import { DraggableProducts } from './DraggableProducts'
import { useProductInfo } from './Product'

type FormikFieldType = {
  equivalentProducts: { gtin: string; referencedTradeItemTypeCode: string }[]
  alternativeProducts: { gtin: string; referencedTradeItemTypeCode: string }[]
}

export const RelatedProducts = () => {
  const [primaryProductModal, setPrimaryProductModal] = useState({
    right: false,
  })
  const [alternativeProductModal, setAlternativeProductModal] = useState({
    right: false,
  })

  const { setAlert } = useAlert()
  const { product, isLoading, error, masterProduct } = useProductInfo()
  const { masterProductId } = useParams()
  const { mutateAsync, isLoading: isLoadingPutRequest } = usePutMasterProduct(
    masterProductId as string
  )
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const handleSubmit = async (values: FormikFieldType) => {
    const masterProductCopy = { ...masterProduct }
    masterProductCopy.tradeItem.referencedTradeItem = [
      ...values.equivalentProducts,
      ...values.alternativeProducts,
    ]

    await mutateAsync(masterProductCopy, {
      onSuccess: () => {
        setAlert(t('saved'), 'success', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  const initialValues = {
    equivalentProducts: product.equivalentProducts,
    alternativeProducts: product.alternativeProducts,
  }

  if (isLoading) return <LoadingIndicator />
  if (error) return <h1>Error</h1>
  return (
    <Box maxWidth='600px'>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={async (values) => handleSubmit(values)}>
        {({ values, dirty }) => (
          <Form style={{ height: '100%' }}>
            <Box mt={7} mb={10}>
              <Box minHeight='300px'>
                <Typography
                  variant='body2'
                  sx={{ color: theme.palette.text.primary }}>
                  {t('equivalentPickingSubstitutions')}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: theme.palette.text.secondary,
                    marginTop: theme.spacing(1),
                  }}>
                  {t('equivalentSubstitutionDescription')}
                </Typography>
                <FieldArray name='equivalentProducts'>
                  {({ push, remove }) => (
                    <>
                      <Box mt={2}>
                        <DraggableProducts
                          remove={remove}
                          type='equivalentProducts'
                          products={values.equivalentProducts}
                        />
                      </Box>
                      <ButtonBase
                        sx={{
                          marginTop: theme.spacing(2),
                          marginLeft: theme.spacing(4),
                        }}
                        onClick={() => setPrimaryProductModal({ right: true })}>
                        <Typography
                          variant='body2'
                          sx={{ color: theme.palette.primary.main }}>
                          {t('addProduct')}
                        </Typography>
                      </ButtonBase>
                      <AddRelatedProductModal
                        modal={primaryProductModal}
                        handleCloseModal={() =>
                          setPrimaryProductModal({ right: false })
                        }
                        handleAddProduct={push}
                        title={t('addEquivalent')}
                        referencedTradeItemTypeCode='EQUIVALENT'
                      />
                    </>
                  )}
                </FieldArray>
              </Box>
              <Box mt={2}>
                <Typography
                  variant='body2'
                  sx={{ color: theme.palette.text.primary }}>
                  {t('primaryAlternativeSimilar')}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: theme.palette.text.secondary,
                    marginTop: theme.spacing(1),
                  }}>
                  {t('alternativeSubstitutionDescription')}
                </Typography>
                <FieldArray name='alternativeProducts'>
                  {({ push, remove }) => (
                    <>
                      <Box mt={2}>
                        <DraggableProducts
                          remove={remove}
                          type='alternativeProducts'
                          products={values.alternativeProducts}
                        />
                      </Box>
                      <ButtonBase
                        sx={{
                          marginTop: theme.spacing(2),
                          marginLeft: theme.spacing(4),
                        }}
                        onClick={() =>
                          setAlternativeProductModal({ right: true })
                        }>
                        <Typography
                          variant='body2'
                          sx={{ color: theme.palette.primary.main }}>
                          {t('addProduct')}
                        </Typography>
                      </ButtonBase>
                      <AddRelatedProductModal
                        modal={alternativeProductModal}
                        handleCloseModal={() =>
                          setAlternativeProductModal({ right: false })
                        }
                        handleAddProduct={push}
                        title={t('addPrimaryAlternative')}
                        referencedTradeItemTypeCode='PRIMARY_ALTERNATIVE'
                      />
                    </>
                  )}
                </FieldArray>
              </Box>
            </Box>
            <Box
              mt={3}
              sx={{
                position: 'fixed',
                bottom: isMobile ? '0px' : theme.spacing(2),
                width: isMobile ? '92%' : '504px',
                zIndex: 1000,
              }}>
              <SaveBtn
                isValid
                dirty={dirty}
                showOverlay
                isLoadingRequest={isLoadingPutRequest}
              />
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
